import { Box, Container, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'redux/app/store';
import { useDispatch } from 'react-redux';
import Head from 'next/head';
import PATHS from 'lib/navigation/paths';
import { setGoogleAnalytics } from 'redux/features/globalData-slice';
import { TOUR_TYPES, GA_EVENT_CATEGORY } from 'lib/utils/CONSTANTS';
import generateMetaData from 'lib/utils/generateMetaData';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { LazyLoad } from 'views/common/components';
import { FILTER_TYPES } from 'views/rebranded-module/tours-module/ToursListingViewV2/utils/constants';
import StyledBox from 'views/rebranded-module/tours-module/ToursListingViewV2/ToursListingViewV2.styles';
import useRenderAiBot from 'views/common/hooks/useRenderAiBot';
import { PAGES_GA_IDENTIFIERS } from 'views/common/utils/constants';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import { ImgBox } from 'views/rebranded-module/common/components';
import {
  HeaderSection,
  SubHeaderSection,
  ResponsiveFilterBar,
  ToursListingSection,
  ToursListingSectionSkeleton,
} from 'views/rebranded-module/tours-module/ToursListingViewV2/components';

const ToursListingViewV2: React.FC<any> = (props) => {
  const { serverData, slugData, tourType } = props;
  const cmsData = useContent();
  const durations = cmsData.ToursListingPage.durationsList;
  const appNavigator = useAppNavigator();
  const { googleAnalytics, isSSR } = useSelector((state) => state.globalData);
  const dispatch = useDispatch();
  useRenderAiBot(true);

  const initFilters = () => {
    const defaultDuration = durations?.[0]?.id;
    const raw =
      typeof window !== 'undefined' ? sessionStorage.getItem('duration') : null;
    const duration = raw ? Number(raw) : defaultDuration;
    if (serverData) {
      return {
        country: serverData.selected_country?.name,
        region: serverData.selected_region?.name,
        market: serverData.selected_market?.name,
        location: serverData.selected_location?.name,
        duration,
        q: appNavigator.query.q,
        slug: slugData?.slugs[0].slug,
        resourceId: slugData?.slugs[0].resource_id,
        slugType: slugData?.slugs[0].slug_type,
      };
    }

    return {
      duration,
      q: appNavigator.query.q,
      slug: slugData?.slugs[0].slug,
      resourceId: slugData?.slugs[0].resource_id,
      slugType: slugData?.slugs[0].slug_type,
    };
  };

  const [pageData, setPageData] = useState(serverData);
  const [filters, setFilters] = useState(initFilters());
  const [filteredTours, setFilteredTours] = useState(serverData?.tours || []);

  const toursFilterList = useMemo(() => {
    if (!pageData) return [];
    const query = filters.q && { [FILTER_TYPES.SEARCH_VALUE]: filters.q };

    return [
      {
        id: 0,
        value: TOUR_TYPES.ALL,
        name: cmsData.ToursListingPage.allTourTypesLabel,
        path: PATHS.motorcycleTours.listing(query),
        pathWithSlug: PATHS.motorcycleTours.slug,
      },
      {
        id: 1,
        value: TOUR_TYPES.GUIDED,
        name: cmsData.ToursListingPage.guidedToursLabel,
        path: PATHS.tour.guidedTourIndex(query),
        pathWithSlug: PATHS.guidedTour,
      },
      {
        id: 2,
        value: TOUR_TYPES.SELF_DRIVE,
        name: cmsData.ToursListingPage.selfGuidedToursLabel,
        path: PATHS.tour.selfTourIndex(query),
        pathWithSlug: PATHS.selfDriveTour,
      },
    ];
  }, [pageData, cmsData]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      country: serverData.selected_country?.name,
      region: serverData.selected_region?.name,
      market: serverData.selected_market?.name,
      location: serverData.selected_location?.name,
      duration: prev.duration,
    }));
    setFilteredTours(serverData.tours);
    setPageData(serverData);
    window.gtag('event', 'landing_page_view', {
      page_identifier: PAGES_GA_IDENTIFIERS.TOURS_LISTING,
    });
  }, [serverData]);

  useEffect(() => {
    dispatch(
      setGoogleAnalytics({
        ...googleAnalytics,
        newsLetterEventCategory: GA_EVENT_CATEGORY.tours_listing_index,
      }),
    );
  }, []);

  useEffect(() => {
    if (isSSR) {
      setPageData(serverData);
    }
    dispatch(
      setGoogleAnalytics({
        ...googleAnalytics,
        newsLetterEventCategory: GA_EVENT_CATEGORY.tours_listing_index,
      }),
    );
  }, [serverData]);

  const getTargetFilterItem = (value, type) => {
    let targetList;
    switch (type) {
      case FILTER_TYPES.COUNTRY:
        targetList = pageData.countries;
        break;
      case FILTER_TYPES.REGION:
        targetList = pageData.regions;
        break;
      case FILTER_TYPES.MARKET:
        targetList = pageData.markets;
        break;
      case FILTER_TYPES.LOCATION:
        targetList = pageData.locations;
        break;
      default:
        targetList = [];
    }
    return targetList.find((item) => item.name === value);
  };
  const redirect = (slug, query) => {
    switch (tourType) {
      case TOUR_TYPES.GUIDED:
        if (slug) {
          appNavigator.push(PATHS.guidedTour(slug, query));
          return PATHS.guidedTour(slug, query).url;
        }
        appNavigator.push(PATHS.guidedTourIndex(query));
        return PATHS.guidedTourIndex(query).url;
      case TOUR_TYPES.SELF_DRIVE:
        if (slug) {
          appNavigator.push(PATHS.selfDriveTour(slug, query));
          return PATHS.selfDriveTour(slug, query).url;
        }
        appNavigator.push(PATHS.selfDriveTourIndex(query));
        return PATHS.selfDriveTourIndex(query).url;
      default:
        if (slug) {
          appNavigator.push(PATHS.motorcycleTours.slug(slug, query));
          return PATHS.motorcycleTours.slug(slug, query).url;
        }
        appNavigator.push(PATHS.motorcycleTours.listing(query));
        return PATHS.motorcycleTours.listing(query).url;
    }
  };
  const prepareNewRouteData = (value, type) => {
    let slug;
    let query;
    let resourceId;
    let slugType;
    if (type === FILTER_TYPES.SEARCH_VALUE) {
      slug = appNavigator.query.slug;
      slugType = filters.slugType;
      query = { [FILTER_TYPES.SEARCH_VALUE]: value };
      resourceId = filters.resourceId;
    } else {
      const targetFilterItem = getTargetFilterItem(value, type);
      slug = targetFilterItem.slug;
      slugType = type;
      query = filters.q && { [FILTER_TYPES.SEARCH_VALUE]: filters.q };
      resourceId = targetFilterItem.id;
    }

    return {
      slug,
      slugType,
      query,
      resourceId,
    };
  };

  useEffect(() => {
    if (!pageData?.tours?.length) {
      setFilteredTours([]);
      return;
    }
    const selectedDuration = durations.find((d) => d.id === filters.duration);
    if (!selectedDuration) {
      setFilteredTours(pageData.tours);
      return;
    }

    const filtered = pageData.tours.filter(
      (tour) =>
        tour.days_count >= selectedDuration.from &&
        tour.days_count <= selectedDuration.to,
    );
    setFilteredTours(filtered);
  }, [filters.duration, pageData?.tours, durations]);

  const handleSearchQueryChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      [FILTER_TYPES.SEARCH_VALUE]: value
    }));
  };

  const handleFilterChange = (value, type) => {
    if (type === FILTER_TYPES.TOUR) {
      if (value === tourType) return;
      const selectedTourType = toursFilterList.find(
        (tourFilterItem) => tourFilterItem.value === value,
      );
      const query = filters.q && { [FILTER_TYPES.SEARCH_VALUE]: filters.q };
      appNavigator.push(
        filters.slug
          ? selectedTourType.pathWithSlug(filters.slug, query)
          : selectedTourType.path,
      );
      return;
    }

    if (
      type === FILTER_TYPES.COUNTRY &&
      value === cmsData.ToursListingPage.allCountriesLabel
    ) {
      const newFilters = {
        ...filters,
        country: null,
        region: null,
        slug: null,
        slugType: null,
        resourceId: null,
      };
      setFilters(newFilters);
      redirect(
        null,
        newFilters.q ? { [FILTER_TYPES.SEARCH_VALUE]: newFilters.q } : {},
      );
      return;
    }

    if (
      type === FILTER_TYPES.REGION &&
      value === cmsData.ToursListingPage.allStatesLabel
    ) {
      const newFilters = {
        ...filters,
        region: null,
      };
      setFilters(newFilters);
      // Keep country slug in path, remove region slug
      const countrySlug =
        getTargetFilterItem(newFilters.country, FILTER_TYPES.COUNTRY)?.slug ||
        null;
      redirect(
        countrySlug,
        newFilters.q ? { [FILTER_TYPES.SEARCH_VALUE]: newFilters.q } : {},
      );
      return;
    }
    if (type === FILTER_TYPES.DURATION) {
      const selectedId = typeof value === 'string' ? Number(value) : value;
      const selected = durations.find((d) => d.id === selectedId);
      if (!selected) return;

      sessionStorage.setItem('duration', String(selectedId));
      setFilters((prev) => ({
        ...prev,
        duration: selectedId,
      }));

      return;
    }
    const { slug, slugType, query, resourceId } = prepareNewRouteData(
      value,
      type,
    );

    setFilters((prev) => ({
      ...prev,
      [type]: value,
      slug,
      slugType,
      resourceId,
    }));

    redirect(slug, query);
  };
  const getTitle = () => {
    let title;
    switch (tourType) {
      case TOUR_TYPES.GUIDED:
        title = cmsData.ToursListingPage.guidedMotorcycleToursLabel;
        break;
      case TOUR_TYPES.SELF_DRIVE:
        title = cmsData.ToursListingPage.selfGuidedMotorcycleToursLabel;
        break;
      default:
        title = cmsData.ToursListingPage.motorcycleToursLabel;
    }

    let filter = '';
    if (filters.country) {
      filter = filters.country;
      if (filters.region) {
        filter = filters.region;
        if (filters.market) {
          filter = filters.market;
          if (filters.location) {
            filter = filters.location;
          }
        }
      }
    }

    if (filter) {
      return {
        title: interpolateString(cmsData.ToursListingPage.pageTitleWithFilter, {
          title,
          filter,
        }),
        subTitle: interpolateString(
          cmsData.ToursListingPage.pageSubTitleWithFilter,
          {
            title,
            filter,
          },
        ),
      };
    }
    return {
      title,
      subTitle: interpolateString(cmsData.ToursListingPage.pageSubTitle, {
        title:
          tourType === TOUR_TYPES.ALL
            ? cmsData.ToursListingPage.subtitleAllmotorcycleToursLabel
            : title,
      }),
    };
  };

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('duration');
    };
  }, []);

  return (
    <>
      <Head>
        {pageData?.seo_values && generateMetaData(pageData.seo_values)}
      </Head>
      <StyledBox>
        <Box className="TLVBGContainer">
          <ImgBox
            image={{
              src: '/imgs/Rebranding/petterns/asphalt.png',
              alt: 'Asphalt background',
            }}
            width="100%"
            height="100%"
          />
        </Box>
        <Box className="TLVContantContainer">
          <Container maxWidth="xxl" className="TLVCustomContainer">
            <HeaderSection title={getTitle().title} />
            <Box className="TLVFilterContainer" my={3}>
              <ResponsiveFilterBar
                filters={filters}
                tourType={tourType}
                handleFilterChange={handleFilterChange}
                countries={pageData?.countries}
                tours={toursFilterList}
                regions={pageData?.regions}
                markets={pageData?.markets}
                locations={pageData?.locations}
                toursCount={pageData?.tours.length}
                toursList={pageData?.tours}
              />
            </Box>
            <SubHeaderSection
              subtitle={getTitle().subTitle}
              toursCount={filteredTours.length}
              handleFilterChange={handleFilterChange}
              updateSearchQueryFilter={handleSearchQueryChange}
            />
            <LazyLoad
              skeleton={<ToursListingSectionSkeleton />}
              dataLoaded={pageData}>
              <ToursListingSection
                tours={pageData?.tours}
                filteredTours={filteredTours}
              />
            </LazyLoad>
            <Typography
              component="p"
              variant="body2"
              className="TLVDescription showMOBILE">
              {cmsData.ToursListingPage.headingDescription}
            </Typography>
          </Container>
        </Box>
      </StyledBox>
    </>
  );
};

export { ToursListingViewV2 };
